import ClearIcon from '@mui/icons-material/Clear'
import SaveIcon from '@mui/icons-material/Save'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import { message } from 'antd'
import { useEffect, useMemo, useState } from 'react'

import { ERRORS_MESSAGE_MAPPER } from '../../../constants/errors'
import { EPLATFORM } from '../../../constants/platform'
import { isAllowShopee, isAllowTikTok } from '../../../constants/user'
import useAxios, { axiosInstance } from '../../../hooks/useAxios'
import { useUser } from '../../../hooks/useUser'
import {
  IPlatformLink,
  IProduct,
  IUsedPlatformLink,
} from '../../../types/product'
import PlatformLinksField from './PlatformLinksField'

interface IProductPlatformLinksModalProps {
  isOpen: boolean
  onClose: (visible: boolean) => void
  product: IProduct
  onUpdateProductSuccess: () => void
}

// แท็บของแต่ละ marketplace — เพิ่ม platform ใหม่ = เพิ่มบรรทัดเดียวตรงนี้
// (ตัวเลือกสินค้าเลือกตาม platform อยู่ใน PlatformLinksField)
const PLATFORM_TABS: {
  platform: string
  label: string
  isAllowed: (merchant: any) => boolean
}[] = [
  { platform: EPLATFORM.TIKTOK, label: 'TikTok', isAllowed: isAllowTikTok },
  { platform: EPLATFORM.SHOPEE, label: 'Shopee', isAllowed: isAllowShopee },
]

// แก้เฉพาะการผูก SKU marketplace — แยกจาก modal แก้ไขสินค้า จึงไม่ต้องกรอก
// ข้อมูลสินค้าทั้งชุดใหม่ และไม่เสี่ยงเขียนทับ field อื่น
export const ProductPlatformLinksModal = (
  props: IProductPlatformLinksModalProps,
) => {
  const { isOpen, onClose, product, onUpdateProductSuccess } = props
  const user = useUser()
  const [links, setLinks] = useState<IPlatformLink[]>([])
  const [isSaving, setIsSaving] = useState<boolean>(false)

  // แสดงเฉพาะ marketplace ที่ร้านมีสิทธิเชื่อม
  const allowedTabs = useMemo(
    () => PLATFORM_TABS.filter((tab) => tab.isAllowed(user.merchant)),
    [user.merchant],
  )
  const [activeTab, setActiveTab] = useState<string>('')

  useEffect(() => {
    setLinks(product?.platformLinks ?? [])
    setActiveTab(allowedTabs[0]?.platform ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, product])

  // SKU ของ platform นี้ที่สินค้าตัวอื่นในร้านผูกไปแล้ว — ใช้ปิดตัวเลือกกันผูกซ้ำ
  // (ผูกซ้ำ = resolver เจอสินค้า >1 ตัว → ambiguous → ตัดสต๊อกไม่ได้ทั้งคู่)
  // ยิงเมื่อเปิด modal + ต่อ platform ที่เปิดดูเท่านั้น
  const { data: usedLinks, mutate: refetchUsedLinks } = useAxios<
    IUsedPlatformLink[]
  >(
    isOpen && activeTab
      ? {
          method: 'GET',
          url: '/product/platform-links/used',
          params: { platform: activeTab },
        }
      : null,
  )

  const countLinks = (platform: string): number =>
    links.filter((link) => link.platform === platform).length

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await axiosInstance.put(`/product/platform-links/${product.objectId}`, {
        platformLinks: links,
      })
      message.success('บันทึกการผูกสินค้าสำเร็จ')
      // ที่เพิ่งผูก/ปลดต้องมีผลกับ dropdown รอบหน้าทันที
      refetchUsedLinks()
      onClose(false)
      onUpdateProductSuccess()
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Dialog
      open={isOpen}
      onClose={() => onClose(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>เชื่อมสินค้ากับ Platform</DialogTitle>
      <DialogContent>
        <Box mb={3}>
          <Typography variant="subtitle1">{product?.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            รหัสสินค้า: {product?.code || '-'}
          </Typography>
        </Box>

        {allowedTabs.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            ร้านนี้ยังไม่ได้เปิดใช้งาน marketplace ใด ๆ
          </Typography>
        ) : (
          <>
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
            >
              {allowedTabs.map((tab) => (
                <Tab
                  key={tab.platform}
                  value={tab.platform}
                  label={`${tab.label}${
                    countLinks(tab.platform)
                      ? ` (${countLinks(tab.platform)})`
                      : ''
                  }`}
                />
              ))}
            </Tabs>
            <PlatformLinksField
              key={activeTab}
              platform={activeTab}
              links={links}
              legacyPlatformId={product?.referrer?.[activeTab]}
              usedLinks={usedLinks ?? []}
              currentProductObjectId={product?.objectId}
              onChange={setLinks}
            />
          </>
        )}

        <Stack direction="row" spacing={2} mt={4} mb={2}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={isSaving}
            onClick={handleSave}
          >
            บันทึก
          </Button>
          <Button
            fullWidth
            variant="outlined"
            color="inherit"
            startIcon={<ClearIcon />}
            onClick={() => onClose(false)}
          >
            ยกเลิก
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

export default ProductPlatformLinksModal
